<template>
  <div>
    <slot />
  </div>
</template>

<script setup>
import { watchOnce } from '@vueuse/core';
import useUrlParams from '@composables/use-url-params';

import {
  ref,
  computed,
  watchEffect,
  inject,
  provide,
  toRefs,
  readonly,
  watch,
} from 'vue';

const LOWEST_PAGE_LIMIT = 1;

const props = defineProps({
  initialPage: {
    type: Number,
    default: 0,
  },
  useHistory: {
    type: Boolean,
    default: true,
  },
});

const { initialPage, useHistory } = toRefs(props);

const params = inject('api/params');
const setOverwriteParam = inject('api/setOverwriteParam');
const lastResponse = inject('api/lastResponse');
const responseHistory = inject('api/responseHistory');

const page = ref(params?.page);
provide('api/paginator/page', readonly(page));

if (useHistory.value) {
  const { params: urlSearchParams } = useUrlParams();

  if (urlSearchParams.page) {
    page.value = Number(decodeURIComponent(urlSearchParams.page));
  }

  watch(page, page => {
    urlSearchParams.page = encodeURIComponent(page);
  });
}

const lowestPage = ref(page.value);
provide('api/paginator/lowestPage', readonly(lowestPage));
const highestPage = ref(page.value);
provide('api/paginator/highestPage', readonly(highestPage));

watchOnce(
  params,
  params => {
    if (page.value) return;

    page.value = params.page || initialPage.value;
    lowestPage.value = page.value;
    highestPage.value = page.value;
  },
  { deep: true, immediate: !params.page }
);

const hasReachedFirstPage = computed(
  () => lowestPage.value === LOWEST_PAGE_LIMIT
);
const canGotoPreviousPage = computed(() => {
  return !hasReachedFirstPage.value;
});
provide('api/paginator/canGotoPreviousPage', readonly(canGotoPreviousPage));

const gotoPreviousPage = () => {
  if (!canGotoPreviousPage.value) return;
  page.value = lowestPage.value - 1;
};
provide('api/paginator/previous', gotoPreviousPage);

const hasReachedLastPageOnce = ref(false);
watchEffect(() => {
  if (lastResponse.value?.lastPage) {
    hasReachedLastPageOnce.value = true;
  }
});
const canGotoNextPage = computed(() => {
  return !hasReachedLastPageOnce.value;
});
provide('api/paginator/canGotoNextPage', readonly(canGotoNextPage));
const gotoNextPage = () => {
  if (!canGotoNextPage.value) return;
  page.value = highestPage.value + 1;
};
provide('api/paginator/next', gotoNextPage);

const gotoFirstPage = () => {
  page.value = 1;
  lowestPage.value = 1;
  highestPage.value = 1;
  hasReachedLastPageOnce.value = false;
};

watch(
  page,
  page => {
    setOverwriteParam('page', page);
    if (lowestPage.value > page) lowestPage.value = page;
    if (highestPage.value < page) highestPage.value = page;
  },
  { immediate: true }
);

const onClearResponseHistory = inject('api/onClearResponseHistory');
onClearResponseHistory(gotoFirstPage);

const isFetching = inject('api/isFetching');
const newPageIsPrevious = ref(false);
watch(page, page => {
  newPageIsPrevious.value = page === lowestPage.value;
});
provide('api/paginator/newPageIsPrevious', newPageIsPrevious);
const isFetchingPreviousContent = computed(() => {
  return (
    isFetching.value &&
    !isFetchingNextContent.value &&
    page.value === lowestPage.value
  );
});
provide('api/paginator/isFetchingPreviousContent', isFetchingPreviousContent);
const newPageIsNext = ref(false);
watch(page, page => {
  newPageIsNext.value = page === highestPage.value;
});
provide('api/paginator/newPageIsNext', newPageIsNext);
const isFetchingNextContent = computed(() => {
  const isInitialRequest = responseHistory.value.length === 0;
  return (
    isFetching.value && (page.value === highestPage.value || isInitialRequest)
  );
});
provide('api/paginator/isFetchingNextContent', isFetchingNextContent);
</script>
